import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { EmvioConfirmation } from '@nexio/emvio-util-ui';
import {
    EmvioButton,
    EmvioValidatedButton,
    EmvioValidatedForm,
    EmvioValidatedInput,
    EmvioMultiSelect,
    EmvioSelect
} from '@nexio/emvio-form-components';

import config from '../../../config/config';
import Modal from '../../Modal/Modal';
import { getRoles } from '../userManagementEntity';
import { getMerchants } from '../../Merchants/merchantsEntity';
import { getPayoutAccounts } from '../../PayoutAccounts/payoutAccountEntity';
import { merchantShape, payoutAccountShape, roleShape } from '../../../utils/propTypes';

import './UserForm.scss';

export const userRoleOptions = [
    { value: 'A', label: 'Administrator', description: 'Manage Users, Access to All Merchants, All Actionable items, View Data & Reports' },
    { value: 'W', label: 'Standard', description: 'All Actionable Items, View Data & Reports' },
    { value: 'R', label: 'View Only', description: 'View Data & Reports' },
    { value: 'API', label: 'API User', description: 'User with preconfigured API key and user name (no dashboard access)' }
];

export class UserForm extends Component {
    static propTypes = {
        isSubmitLoading: PropTypes.bool,
        isDeleteLoading: PropTypes.bool,
        isEdit: PropTypes.bool,
        isApiUser: PropTypes.bool,
        username: PropTypes.string,
        role: PropTypes.string,
        customRoles: PropTypes.arrayOf(roleShape),
        apiKey: PropTypes.string,
        merchantIds: PropTypes.array,
        merchants: PropTypes.arrayOf(merchantShape),
        payoutAccountIds: PropTypes.array,
        payoutAccounts: PropTypes.arrayOf(payoutAccountShape),
        onDelete: PropTypes.func,
        onUsernameChange: PropTypes.func,
        onMerchantsChange: PropTypes.func.isRequired,
        onPayoutAccountsChange: PropTypes.func.isRequired,
        onOpen: PropTypes.func,
        onSelectAccessRole: PropTypes.func.isRequired,
        onSubmit: PropTypes.func.isRequired,
        modalTitle: PropTypes.string.isRequired,
        uniqueModalName: PropTypes.string.isRequired
    };

    static defaultProps = {
        merchantIds: [],
        merchants: [],
        payoutAccountIds: [],
        payoutAccounts: [],
        customRoles: [],
        onUsernameChange: () => { },
        onMerchantsChange: () => { },
        onPayoutAccountsChange: () => { },
        onOpen: () => { },
        onDelete: () => { }
    };

    constructor(props) {
        super(props);

        this.submitButtonRef = React.createRef();
        this.deleteButtonRef = React.createRef();
        this.popoverDeleteUserRef = React.createRef();
    }

    handleDeleteUser = () => {
        this.popoverDeleteUserRef.current.openPopover(this.deleteButtonRef.current);
    };

    renderUsername = () => {
        const { isEdit, isApiUser } = this.props;

        const isDisabled = isEdit || isApiUser;

        let validations = ['required', 'username'];

        if (isDisabled) {
            validations = [];
        }

        if (isEdit || !isApiUser) {
            return (
                <EmvioValidatedInput
                    className="username-field"
                    disabled={isDisabled}
                    invert
                    name="username"
                    labelText="USERNAME"
                    onChange={this.props.onUsernameChange}
                    type="input"
                    value={this.props.username || ''}
                    validations={validations}
                />
            );
        }
    };

    renderUserRole = () => {
        const { isEdit, isApiUser, customRoles } = this.props;

        const customRoleOptions = customRoles.map((role) => ({
            value: role.role,
            label: role.name,
            description: role.description
        }));

        const roleOptions = [...userRoleOptions, ...customRoleOptions];

        const selectedRole = roleOptions.find((role) => role.value === this.props.role);

        const isEditingApiUser = isEdit && isApiUser;

        let roleDescription;
        let accountRoleInput;
        if (isEditingApiUser) {
            accountRoleInput = (
                <EmvioValidatedInput
                    className="user-role"
                    disabled={true}
                    name="role"
                    labelText="ACCOUNT ROLE"
                    onChange={_.noop}
                    type="input"
                    value="API User"
                    validations={[]}
                    readOnly
                />
            );
        } else if (isEdit) {
            accountRoleInput = (
                <EmvioSelect
                    className="user-role-selector"
                    label="ACCOUNT ROLE"
                    options={roleOptions.filter((role) => role.value !== 'API')}
                    onChange={this.props.onSelectAccessRole}
                    defaultSelectedOption={selectedRole}
                />
            );

            roleDescription = _.get(selectedRole, 'description');
        } else {
            accountRoleInput = (
                <EmvioSelect
                    className="user-role-selector"
                    label="ACCOUNT ROLE"
                    options={roleOptions}
                    onChange={this.props.onSelectAccessRole}
                    defaultSelectedOption={selectedRole}
                />
            );

            roleDescription = _.get(selectedRole, 'description');
        }

        return (
            <div className="role-select-wrapper">
                {accountRoleInput}
                <div className="role-description">
                    {roleDescription}
                </div>
            </div>
        );
    };

    renderApiKey = () => {
        const { isEdit, isApiUser } = this.props;

        if (isEdit && isApiUser) {
            return <EmvioValidatedInput
                disabled={true}
                invert
                name="apiKey"
                labelText="API KEY"
                onChange={_.noop}
                type="input"
                value={this.props.apiKey}
                validations={[]}
            />;
        }
    };

    renderMerchantSelector = () => {
        const isRoleAdmin = this.props.role === 'A';

        const merchantOptions = this.props.merchants.map((merchant) => ({
            detail: merchant.merchantId,
            label: merchant.name,
            value: merchant.merchantId
        }));

        const { isEdit, isApiUser } = this.props;

        return (
            <div className="user-merchant-select">
                {!isRoleAdmin
                    ? <EmvioMultiSelect
                        disabled={isEdit && isApiUser}
                        defaultSelectedOptions={merchantOptions.filter((option) => this.props.merchantIds.includes(option.value))}
                        label="Merchants"
                        onChange={this.props.onMerchantsChange}
                        options={merchantOptions}
                        required
                    />
                    : null
                }
            </div>
        );
    };

    renderPayoutAccountSelector = () => {
        const isRoleAdmin = this.props.role === 'A';

        if (_.isEmpty(this.props.payoutAccounts)) {
            return null;
        }

        const payoutAccountOptions = this.props.payoutAccounts.map((payoutAccount) => ({
            detail: payoutAccount.payoutAccountId,
            label: payoutAccount.name,
            value: _.toString(payoutAccount.payoutAccountId)
        }));

        const { isEdit, isApiUser } = this.props;

        return (
            <div className="user-payout-account-select">
                {!isRoleAdmin
                    ? <EmvioMultiSelect
                        disabled={isEdit && isApiUser}
                        defaultSelectedOptions={payoutAccountOptions.filter((option) => this.props.payoutAccountIds.includes(option.value))}
                        label="Payout Accounts"
                        onChange={this.props.onPayoutAccountsChange}
                        options={payoutAccountOptions}
                    />
                    : null
                }
            </div>
        );
    };

    renderFooter = () => {
        const { isEdit, isApiUser } = this.props;

        let footerContent;

        if (isEdit && isApiUser) {
            footerContent = (
                <div className="message">
                    <span>To update or delete this user, please reach out to Integrations Support at</span>
                    <a href="mailto:integrations@nex.io">{config.integrationsEmail}</a>.
                </div>
            );

        } else if (isEdit) {
            footerContent = [];

            footerContent.push(
                <EmvioButton
                    className="user-button"
                    ref={this.deleteButtonRef}
                    invertColors
                    isLoading={this.props.isDeleteLoading}
                    onClick={this.handleDeleteUser}
                    key="deleteButton"
                    size="large"
                    variant="secondary"
                    type="button"
                >
                    Delete User
                </EmvioButton>
            );

            footerContent.push(
                <EmvioConfirmation
                    buttonText="Yes"
                    key="delete-confirmation"
                    onConfirm={this.props.onDelete}
                    text="Delete Nexio User?"
                    ref={this.popoverDeleteUserRef}
                />
            );

            footerContent.push(
                <EmvioValidatedButton
                    className="user-button"
                    key="saveButton"
                    invertColors
                    isLoading={this.props.isSubmitLoading}
                    ref={this.submitButtonRef}
                    size="large"
                >
                    Save User
                </EmvioValidatedButton>
            );
        } else {
            footerContent = (
                <EmvioValidatedButton
                    className="user-button"
                    key="saveButton"
                    invertColors
                    isLoading={this.props.isSubmitLoading}
                    ref={this.submitButtonRef}
                    size="large"
                >
                    Add User To Account
                </EmvioValidatedButton>
            );
        }

        return (
            <div className="user-form-footer">
                {footerContent}
            </div>
        );
    };

    render() {
        const isRoleAdmin = this.props.role === 'A';

        return (
            <Modal
                uniqueModalName={this.props.uniqueModalName}
                title={this.props.modalTitle}
                onOpen={this.props.onOpen}
            >
                <EmvioValidatedForm
                    className="emvio-user-form form-container"
                    getButtonRef={() => this.submitButtonRef.current}
                    hasExternalErrors={(!this.props.merchantIds.length && !isRoleAdmin) || !this.props.role}
                    onSubmit={this.props.onSubmit}>
                    <div className="input-wrapper">
                        {this.renderUserRole()}
                        {this.renderUsername()}
                        {this.renderApiKey()}
                        {this.renderMerchantSelector()}
                        {this.renderPayoutAccountSelector()}
                    </div>
                    {this.renderFooter()}
                </EmvioValidatedForm>
            </Modal>
        );
    }
}

function mapStateToProps(state) {
    return {
        merchants: getMerchants(state),
        payoutAccounts: getPayoutAccounts(state),
        customRoles: getRoles(state)
    };
}

export default connect(mapStateToProps)(UserForm);
